/* eslint-disable react/jsx-props-no-spreading */
// @ts-nocheck

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  Polygon,
  InfoWindow,
  Autocomplete,
  Marker,
} from '@react-google-maps/api';

import TextField from '@mui/material/TextField';

import { useNavigate, useParams } from 'react-router-dom';

import { Appbar } from '@Components/app-bar';

import { Box, styled } from '@mui/material';
import CustomerFormModal from './CustomerFormModal';

import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';

import resolver, { IPolygonFormData } from './CustomerForm.validation';

import {
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
} from '@Store/api/customersSlice';
import { toast } from 'react-toastify';
import { handleFetchError } from '@Utils/handleError';

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MapContainer = styled(Box)`
  width: 100%;
  height: calc(100vh - 120px);
  padding: 30px;
  position: relative;
`;

const StyledAotuCompleteInput = styled(TextField)`
  .MuiInputBase-root {
    border-radius: 30px;
    background: #fff;
    margin-left: 10px;
    min-width: 550px;
  }
`;

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: 30,
};

const defaultCenter = {
  lat: 51.509865,
  lng: -0.118092,
};

const polygonOptions = {
  fillOpacity: 0.3,
  strokeWeight: 2,
  draggable: true,
  editable: true,
};

// Replacement for Google's deprecated DrawingManager toolbar: same position (map top-right),
// same two tools (hand = move the map, polygon = draw a shape), same look.
const drawingButtonStyle = (active: boolean, hover: boolean): React.CSSProperties => ({
  width: 32,
  height: 32,
  padding: 0,
  border: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: hover ? '#ebebeb' : '#fff',
  color: active ? '#000' : '#999',
});

const DrawingControl = ({
  mode,
  onChange,
}: {
  mode: 'hand' | 'polygon';
  onChange: (mode: 'hand' | 'polygon') => void;
}) => {
  const [hover, setHover] = useState<'hand' | 'polygon' | null>(null);

  return (
    <div
      style={{
        display: 'flex',
        background: '#fff',
        borderRadius: 2,
        boxShadow: 'rgba(0, 0, 0, 0.3) 0 1px 4px -1px',
        overflow: 'hidden',
      }}
    >
      <button
        aria-label='Stop drawing'
        style={drawingButtonStyle(mode === 'hand', hover === 'hand')}
        title='Stop drawing'
        type='button'
        onClick={() => onChange('hand')}
        onMouseEnter={() => setHover('hand')}
        onMouseLeave={() => setHover(null)}
      >
        <svg fill='currentColor' height='20' viewBox='0 0 24 24' width='20'>
          <rect height='10' rx='1.4' width='2.8' x='5.6' y='4' />
          <rect height='12' rx='1.4' width='2.8' x='8.8' y='2' />
          <rect height='11.5' rx='1.4' width='2.8' x='12' y='2.5' />
          <rect height='9' rx='1.4' width='2.8' x='15.2' y='5' />
          <rect height='9' rx='3.5' width='12.4' x='5.6' y='11' />
          <rect
            height='2.8'
            rx='1.4'
            transform='rotate(-35 6 12.5)'
            width='6.5'
            x='2.5'
            y='11.2'
          />
        </svg>
      </button>
      <button
        aria-label='Draw a shape'
        style={drawingButtonStyle(mode === 'polygon', hover === 'polygon')}
        title='Draw a shape'
        type='button'
        onClick={() => onChange('polygon')}
        onMouseEnter={() => setHover('polygon')}
        onMouseLeave={() => setHover(null)}
      >
        <svg fill='none' height='20' viewBox='0 0 24 24' width='20'>
          <polygon
            points='12,3.5 20.5,9.5 17.5,19.5 6.5,19.5 3.5,9.5'
            stroke='currentColor'
            strokeLinejoin='round'
            strokeWidth='1.8'
          />
          {[
            [12, 3.5],
            [20.5, 9.5],
            [17.5, 19.5],
            [6.5, 19.5],
            [3.5, 9.5],
          ].map(([cx, cy]) => (
            <circle
              cx={cx}
              cy={cy}
              fill='#fff'
              key={`${cx}-${cy}`}
              r='2'
              stroke='currentColor'
              strokeWidth='1.5'
            />
          ))}
        </svg>
      </button>
    </div>
  );
};

export interface IDataToSend extends IPolygonFormData {
  selections: IPolygonData[] & {
    areaMap: string;
  };
}

export const SpaceTarrifMapNewCustomer = () => {
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [infoWindowDataIndex, setInfoWindowDataIndex] = useState(-1);

  const params = useParams();

  const navigate = useNavigate();

  const {
    data: customerData,
    error,
    isSuccess,
  } = useGetCustomerQuery(params.id, {
    skip: !params.id,
  });

  const [createCustomer] = useCreateCustomerMutation();

  const [updateCustomer] = useUpdateCustomerMutation();

  const mapRef = useRef();
  const polygonRefs = useRef([]);
  const activePolygonIndex = useRef();
  const [drawingMode, setDrawingMode] = useState<'hand' | 'polygon'>('hand');
  const onPolygonDrawnRef = useRef();

  const [center, setCenter] = useState(defaultCenter);
  const [autocomplete, setAutocomplete] = useState(null); 
  
  const [markerPosition, setMarkerPosition] = useState(null); // State for marker position

  const methods = useForm<IPolygonFormData>({
    defaultValues: {
      selections: [],
    },
    mode: 'all',
    resolver,
  });

  const { control, setValue, watch } = methods;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'selections',
  });

  const defaultReminderFee = watch('defaultReminderFee');
  const defaultGraceTime = watch('defaultGraceTime');
  const vatNumber = watch('vatNumber');
  const defaultConvenienceFee = watch('defaultConvenienceFee');

  const watchFieldArray = watch('selections');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  useEffect(() => {
    if (isSuccess && customerData) {
      setValue('name', customerData.name);
      setValue('companyEmail', customerData.companyEmail);
      setValue('vatNumber', customerData.vatNumber);
      setValue('defaultReminderFee', customerData.defaultReminderFee);
      setValue('defaultConvenienceFee', customerData.defaultConvenienceFee);
      setValue('merchantId', customerData.merchantId);
      setValue('selections', customerData.selections);
      setValue('defaultGraceTime', customerData.defaultGraceTime);
      setValue('companyAddress', customerData.companyAddress);
      setValue('companyCity', customerData.companyCity);
      setValue('companyRegion', customerData.companyRegion);
      setValue('companyPostCode', customerData.companyPostCode);
      setValue(
        'sendFinancialEmailAutomatically',
        customerData.sendFinancialEmailAutomatically
      );
      setValue(
        'emailScheduleDayOfMonth',
        customerData.emailScheduleDayOfMonth ?? null
      );

      const firstSelection = customerData.selections[0];

      if (firstSelection) {
        if (firstSelection.coordinates[0]) {
          setCenter(firstSelection.coordinates[0]);
        }
      }
    }
  }, [isSuccess, customerData, setValue]);

  useEffect(() => {
    handleFetchError(error);
  }, [error]);

  useEffect(() => {
    if (!vatNumber && controlledFields?.length) {
      controlledFields.forEach((selection, i) => {
        update(i, { ...selection, hasVat: false });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatNumber]);

  const editing = useMemo(() => {
    return !!params.id;
  }, [params]);

  const onLoad = (autocomplete) => {

    setAutocomplete(autocomplete);
  
  };
  
  const onPlaceChanged = () => {
    
    if (autocomplete !== null) {
  
      const place = autocomplete.getPlace();
      
      if (place.geometry) {
  
        setCenter({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
        const newPosition = {
          lat: place.geometry.location.lat(),
          
          lng: place.geometry.location.lng()
        
        };
        
        
        setMarkerPosition(newPosition); // Set marker at searched location

        // Optional: Pan to the location
        if (mapRef.current) {  
          mapRef.current.panTo(place.geometry.location);
        }
      }
    }
  };

  const onPolygonClick = (index) => {
    setInfoWindowOpen(true);
    setInfoWindowDataIndex(index);

    document
      .getElementById(`selection-${index + 1}`)
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowOpen(false);
    setInfoWindowDataIndex(-1);
  };

  const onLoadMap = (map) => {
    mapRef.current = map;
  };

  const onLoadPolygon = (polygon, index) => {
    polygonRefs.current[index] = polygon;
  };

  const onClickPolygon = (index) => {
    activePolygonIndex.current = index;
  };

  // Called when the user finishes drawing a polygon. Same result as the old DrawingManager
  // "overlaycomplete" handler: the points (closed by repeating the first one) become a new selection.
  const handlePolygonDrawn = (points) => {
    const newPolygon = [...points];
    const newSelectionId = `selection-${fields.length + 1}`;

    const startPoint = newPolygon[0];
    newPolygon.push(startPoint);
    append({
      name: '',
      vatNumber: '',
      merchantId: '',
      coordinates: newPolygon,
      hasVat: false,
      reminderFee: defaultReminderFee,
      graceTime: defaultGraceTime,
      convenienceFee: defaultConvenienceFee,
      defaultTime: 0,
      front_id: Math.random(),
      isVisibleOnAppMap: true,
      surroundingsInfo: '',
      tariffDisplayMode: 2,
      evChargePoints: null,
      disabledBays: null,
      heightRestriction: null,
      hasLifts: false,
      hasToilets: false,
      freeText: '',
    });

    // Take the user to the new entry in the form once it has rendered, ready to type into.
    setTimeout(() => {
      const element = document.getElementById(newSelectionId);

      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      element?.querySelector('input')?.focus({ preventScroll: true });
    }, 100);
  };

  // Always point at the latest handler so the drawing listeners (set up once per drawing session)
  // use the current default fees.
  onPolygonDrawnRef.current = handlePolygonDrawn;

  // Polygon drawing mode (replaces google.maps.drawing.DrawingManager): click to add points,
  // click the first point or double-click to finish, then go back to the hand tool.
  useEffect(() => {
    const map = mapRef.current;

    if (!map || drawingMode !== 'polygon') return undefined;

    const points = [];
    const mouse = { latLng: null };

    // Open line (not a filled shape) that follows the cursor, like the old drawing tool.
    const preview = new google.maps.Polyline({
      strokeWeight: polygonOptions.strokeWeight,
      clickable: false,
      map,
      path: [],
    });

    const startMarker = new google.maps.Marker({
      clickable: true,
      cursor: 'pointer',
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: '#fff',
        fillOpacity: 1,
        strokeColor: '#000',
        strokeWeight: 1.5,
      },
      zIndex: 1000,
    });

    // True when latLng is within ~12 screen pixels of the first point, at the current zoom.
    const isNearFirstPoint = (latLng) => {
      const projection = map.getProjection();

      if (!projection || !points.length) return false;

      const a = projection.fromLatLngToPoint(points[0]);
      const b = projection.fromLatLngToPoint(latLng);
      const pixels = Math.hypot(a.x - b.x, a.y - b.y) * 2 ** map.getZoom();

      return pixels <= 12;
    };

    const redraw = () => {
      if (!mouse.latLng) {
        preview.setPath([...points]);
      } else if (points.length >= 3 && isNearFirstPoint(mouse.latLng)) {
        // Close to the start: snap the line onto the first point so it is clear the shape will close.
        preview.setPath([...points, points[0]]);
      } else {
        preview.setPath([...points, mouse.latLng]);
      }
    };

    const finish = () => {
      if (points.length < 3) return;

      onPolygonDrawnRef.current(
        points.map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }))
      );
      setDrawingMode('hand');
    };

    map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });

    const listeners = [
      map.addListener('click', (event) => {
        // Clicking on/near the first point closes the shape (like the old drawing tool).
        if (points.length >= 3 && isNearFirstPoint(event.latLng)) {
          finish();

          return;
        }

        const last = points[points.length - 1];

        // A double-click fires two clicks at the same spot: ignore the repeat.
        if (last && last.equals(event.latLng)) return;

        points.push(event.latLng);

        if (points.length === 1) {
          startMarker.setPosition(event.latLng);
          startMarker.setMap(map);
        }

        redraw();
      }),
      map.addListener('mousemove', (event) => {
        mouse.latLng = event.latLng;
        redraw();
      }),
      map.addListener('dblclick', finish),
      startMarker.addListener('click', finish),
    ];

    return () => {
      listeners.forEach((listener) => listener.remove());
      preview.setMap(null);
      startMarker.setMap(null);
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
    };
  }, [drawingMode]);

  const onEditPolygon = (index) => {
    const polygonRef = polygonRefs.current[index];

    if (polygonRef) {
      const coordinates = polygonRef
        .getPath()
        .getArray()
        .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

      const allPolygons = [...fields];
      allPolygons[index] = coordinates;

      update(index, {
        ...controlledFields[index],
        coordinates: coordinates,
      });
    }
  };

  const onRemovePolygon = (polygonIndex) => {
    remove(polygonIndex);
  };

  const onSelectionClick = (index: number) => {
    if (fields[index].coordinates[0]) setCenter(fields[index].coordinates[0]);
  };

  const handleVATToggleChange = (index: number, isChecked: boolean) => {
    update(index, {
      ...controlledFields[index],
      hasVat: isChecked,
    });
  };

  const handleAppletToggleChange = (index: number, isChecked: boolean) => {
    update(index, {
      ...controlledFields[index],
      hasMiniApp: isChecked,
    });
  };

  const handleVisibleOnAppToggleChange = (index: number, isChecked: boolean) => {
    update(index, {
      ...controlledFields[index],
      isVisibleOnAppMap: isChecked,
    });
  };

  const handleCancellationToggleChange = (
    index: number,
    isChecked: boolean
  ) => {
    update(index, {
      ...controlledFields[index],
      isEndSessionDisabled: !isChecked,
    });
  };

  const handleFormSubmit: SubmitHandler<IPolygonFormData> = async (data) => {
    const dataToSend: IDataToSend = {
      ...data,
      defaultGraceTime: data.defaultGraceTime || 0,
      emailScheduleDayOfMonth: data.emailScheduleDayOfMonth || null,
      selections: data.selections.map(
        ({
          _front_id,
          evChargePoints: _evChargePoints,
          disabledBays: _disabledBays,
          heightRestriction: _heightRestriction,
          hasLifts: _hasLifts,
          hasToilets: _hasToilets,
          freeText: _freeText,
          ...rest
        }) => ({
          ...rest,
          reminderFee: rest.reminderFee || 0,
          convenienceFee: rest.convenienceFee || 0,
          graceTime: rest.graceTime || 0,
          defaultTime: rest.defaultTime || 0,
          areaMap: JSON.stringify(rest.coordinates), // Convert coordinates to string and assign to areaMap
        })
      ),
    };

    try {
      if (editing) {
        await updateCustomer({ ...dataToSend, id: customerData.id }).unwrap();
        toast.success('Customer details updated successfully');
      } else {
        const customer = await createCustomer(dataToSend).unwrap();
        navigate(`/space-tarrif-map/customer/${customer.id}`);
        toast.success('Customer created successfully');
      }
    } catch (err) {
      const message =
        err?.data?.message || err?.message || 'Something went wrong...';

      toast.error(message);
    }
  };

  const infoWindowData =
    infoWindowDataIndex > -1 ? controlledFields[infoWindowDataIndex] : null;

  return (
    <Container>
      <Appbar
        subTitle={editing ? undefined : 'Select Area'}
        title={editing ? customerData?.name : 'New Customer'}
      />

          <div style={{ marginBottom: '20px' }}>
        {/* Correct Autocomplete usage with single child */}
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
        >
          <StyledAotuCompleteInput
      
          placeholder='Search...'
          sx={{
          width: 550,
          }}
          variant='outlined'
      
          />
        </Autocomplete>
      </div>      
      <MapContainer>
        <CustomerFormModal
          controlledFields={controlledFields}
          convenienceFee={defaultConvenienceFee}
          customerId={customerData?.id}
          editing={editing}
          fields={fields}
          graceTime={defaultGraceTime}
          methods={methods}
          reminderFee={defaultReminderFee}
          vatNumber={vatNumber}
          handleFormSubmit={handleFormSubmit}
          handleSelectionClick={onSelectionClick}
          handleAppletToggleChange={handleAppletToggleChange}
          handleVisibleOnAppToggleChange={handleVisibleOnAppToggleChange}
          handleCancellationToggleChange={handleCancellationToggleChange}
          onRemovePolygon={onRemovePolygon}
          onVATToggleChange={handleVATToggleChange}
        />
        <GoogleMap
          center={center}
          mapContainerStyle={containerStyle}
          zoom={15}
          onLoad={onLoadMap}
          onTilesLoaded={() => setCenter(null)}
        >
          {fields.map((field, index) => (
            <Polygon
              draggable={drawingMode === 'hand'}
              editable={drawingMode === 'hand'}
              key={index}
              options={{
                ...polygonOptions,
                // While the polygon tool is on, existing shapes must ignore the mouse so the new
                // line can cross them without grabbing their handles (as the old drawing tool did).
                clickable: drawingMode === 'hand',
                draggable: drawingMode === 'hand',
                editable: drawingMode === 'hand',
              }}
              paths={field.coordinates}
              onClick={() => onPolygonClick(index)}
              onDragEnd={() => onEditPolygon(index)}
              onLoad={(event) => onLoadPolygon(event, index)}
              onMouseDown={() => onClickPolygon(index)}
              onMouseUp={() => onEditPolygon(index)}
            />
          ))}
          {infoWindowOpen && infoWindowData ? (
            <InfoWindow
              position={{
                lat: infoWindowData.coordinates[0].lat,
                lng: infoWindowData.coordinates[0].lng,
              }}
              onCloseClick={handleCloseInfoWindow}
            >
              <div>
                <p>
                  <b>Subarea name:</b> {infoWindowData.subareaName}
                </p>
                <p>
                  <b>Subarea ID:</b> {infoWindowData.subAreaId}
                </p>
                <p>
                  <b>Public Area Id:</b> {infoWindowData.publicId}
                </p>
                <p>
                  <b>Config ID:</b> {infoWindowData.configId}
                </p>
                <p>
                  <b>Default Time (minutes):</b> {infoWindowData.defaultTime}
                </p>
                <p>
                  <b>Reminder Fee:</b> {infoWindowData.reminderFee}
                </p>
                <p>
                  <b>Convenience Fee:</b> {infoWindowData.convenienceFee}
                </p>
                <p>
                  <b>Has VAT:</b> {infoWindowData.hasVat ? 'True' : 'False'}
                </p>
                {infoWindowData.surroundingsInfo ? (
                  <p>
                    <b>Nearby Landmarks:</b> {infoWindowData.surroundingsInfo}
                  </p>
                ) : null}
              </div>
            </InfoWindow>
          ) : null,
          markerPosition && (
          <Marker 
            position={markerPosition}
            animation={window.google.maps.Animation.DROP} // Nice drop animation
          />
        )}
        </GoogleMap>
        {/* Drawing toolbar: sits in the gap to the right of the customer form, just under the fullscreen button. */}
        <div style={{ position: 'absolute', top: 88, right: 38, zIndex: 2 }}>
          <DrawingControl mode={drawingMode} onChange={setDrawingMode} />
        </div>
      </MapContainer>
    </Container>
  );
};
